import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Users, Target, Handshake, Shield, Truck, Clock, ChevronLeft, ChevronRight } from 'lucide-react';

const SLIDES = [
  { src: '/slides/slide-login.png', label: 'Sign In', caption: 'One-click demo access or create your account in seconds' },
  { src: '/slides/slide-intake.png', label: 'Submit Claim', caption: 'Guided multi-step intake form captures every detail' },
  { src: '/slides/slide-dashboard.png', label: 'Track & Analyze', caption: 'Real-time dashboard with charts, stats, and filters' },
  { src: '/slides/slide-entries.png', label: 'Manage & Close', caption: 'Full claims table with status tracking from New to Closed' },
];

const STORY_IMG = 'https://images.unsplash.com/photo-1769740333462-9a63bfa914bc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMHRlYW0lMjBtZWV0aW5nJTIwb2ZmaWNlJTIwcHJvZmVzc2lvbmFsfGVufDB8fHx8MTc3Mzg4Nzk3N3ww&ixlib=rb-4.1.0&q=85';
const GOAL_IMG = 'https://images.unsplash.com/photo-1758599543152-a73184816eba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGhhbmRzaGFrZSUyMHBhcnRuZXJzaGlwJTIwZGVhbHxlbnwwfHx8fDE3NzM4ODc5Nzl8MA&ixlib=rb-4.1.0&q=85';

const values = [
  { icon: Shield, title: 'Industry Expertise', desc: 'Over 25 years of combined experience in trucking safety, compliance, and claims management.' },
  { icon: Clock, title: 'Timely Resolution', desc: 'We prioritize fast and fair claim settlements to keep your operations running smoothly.' },
  { icon: Truck, title: 'Trucking Focused', desc: 'We specialize exclusively in the trucking sector, understanding its unique challenges.' },
];

function HeroSlideshow() {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);

  const next = useCallback(() => setCurrent((c) => (c + 1) % SLIDES.length), []);
  const prev = useCallback(() => setCurrent((c) => (c - 1 + SLIDES.length) % SLIDES.length), []);

  useEffect(() => {
    if (paused) return;
    const id = setInterval(next, 4500);
    return () => clearInterval(id);
  }, [paused, next]);

  return (
    <div
      className="relative w-full h-[380px] sm:h-[460px] lg:h-[500px] bg-gray-900 overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="hero-slideshow"
    >
      {/* Slides */}
      {SLIDES.map((slide, i) => (
        <div
          key={slide.label}
          className="absolute inset-0 transition-opacity duration-700 ease-in-out"
          style={{ opacity: i === current ? 1 : 0, zIndex: i === current ? 1 : 0 }}
        >
          <img
            src={slide.src}
            alt={slide.label}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/70 to-gray-900/40" />
        </div>
      ))}

      {/* Content overlay */}
      <div className="relative z-10 h-full max-w-5xl mx-auto px-4 flex flex-col justify-end pb-10 sm:pb-14">
        <Link to="/landing" className="absolute top-6 left-4 inline-flex items-center gap-1.5 text-sm text-teal-300 hover:text-teal-200 w-fit" data-testid="about-back-link">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>

        <h1 className="text-4xl sm:text-5xl font-bold text-white mb-2" data-testid="about-heading">All About Us</h1>
        <p className="text-base sm:text-lg text-gray-300 max-w-xl mb-6">Your trusted partner for unresolved trucking claims. We bring decades of expertise to every case.</p>

        {/* Slide indicators + caption */}
        <div className="flex items-center gap-4">
          {/* Prev/Next */}
          <button onClick={prev} className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors" data-testid="slide-prev">
            <ChevronLeft className="w-4 h-4 text-white" />
          </button>

          {/* Step pills */}
          <div className="flex items-center gap-2">
            {SLIDES.map((slide, i) => (
              <button
                key={slide.label}
                onClick={() => setCurrent(i)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  i === current
                    ? 'bg-teal-500 text-white shadow-lg shadow-teal-500/30'
                    : 'bg-white/10 text-white/60 hover:bg-white/20 hover:text-white/80'
                }`}
                data-testid={`slide-dot-${i}`}
              >
                <span className="w-4 h-4 rounded-full bg-white/20 flex items-center justify-center text-[10px] font-bold">{i + 1}</span>
                <span className="hidden sm:inline">{slide.label}</span>
              </button>
            ))}
          </div>

          <button onClick={next} className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors" data-testid="slide-next">
            <ChevronRight className="w-4 h-4 text-white" />
          </button>
        </div>

        {/* Active slide caption */}
        <p className="text-sm text-gray-400 mt-3 h-5 transition-opacity duration-300" data-testid="slide-caption">
          {SLIDES[current].caption}
        </p>
      </div>
    </div>
  );
}

export default function AboutUs() {
  return (
    <div className="min-h-screen bg-white" data-testid="about-us-page">
      {/* Hero Slideshow */}
      <HeroSlideshow />

      {/* Our Story */}
      <section className="max-w-5xl mx-auto px-4 py-16 sm:py-20" data-testid="about-story-section">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div className="rounded-xl overflow-hidden shadow-lg order-2 md:order-1">
            <img src={STORY_IMG} alt="Professional team meeting" className="w-full h-64 sm:h-80 object-cover" />
          </div>
          <div className="order-1 md:order-2">
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-5 h-5 text-teal-600" />
              <span className="text-xs font-semibold uppercase tracking-wider text-teal-600">Who We Are</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Our Story</h2>
            <p className="text-gray-600 leading-relaxed">
              Our company was founded by a team of transportation industry professionals with over 25 years of combined experience. Our team comprises individuals with key roles in Safety and Compliance, Claims Management, Equipment Maintenance, Operations, Executive and Upper Management positions.
            </p>
            <p className="text-gray-600 leading-relaxed mt-4">
              The founders recognized the pressing need for a specialized company to deal with unresolved claims in the trucking sector, and thus, All About Claims was established.
            </p>
          </div>
        </div>
      </section>

      {/* Our Mission */}
      <section className="bg-gray-50" data-testid="about-mission-section">
        <div className="max-w-5xl mx-auto px-4 py-16 sm:py-20">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Target className="w-5 h-5 text-teal-600" />
                <span className="text-xs font-semibold uppercase tracking-wider text-teal-600">What Drives Us</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Our Mission</h2>
              <p className="text-gray-600 leading-relaxed">
                By leveraging our expertise and industry knowledge, we strive to deliver not only favorable outcomes but also peace of mind to our clients, empowering them to focus on their core operations with confidence.
              </p>
              <p className="text-gray-600 leading-relaxed mt-4">
                We understand that every claim represents a real impact on your business. That's why we treat each case with the urgency and attention it deserves, working tirelessly to achieve the best possible resolution.
              </p>
            </div>
            <div className="rounded-xl overflow-hidden shadow-lg bg-gradient-to-br from-teal-500 to-teal-700 p-8 text-center">
              <Target className="w-12 h-12 text-white/80 mx-auto mb-4" />
              <p className="text-white text-lg font-medium leading-relaxed italic">
                "Empowering trucking companies to focus on their core operations with confidence."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Goal */}
      <section className="max-w-5xl mx-auto px-4 py-16 sm:py-20" data-testid="about-goal-section">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div className="rounded-xl overflow-hidden shadow-lg order-2 md:order-1">
            <img src={GOAL_IMG} alt="Business partnership handshake" className="w-full h-64 sm:h-80 object-cover" />
          </div>
          <div className="order-1 md:order-2">
            <div className="flex items-center gap-2 mb-3">
              <Handshake className="w-5 h-5 text-teal-600" />
              <span className="text-xs font-semibold uppercase tracking-wider text-teal-600">Where We're Headed</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Our Goal</h2>
            <p className="text-gray-600 leading-relaxed">
              Our goal is to become the trusted partner for trucking companies in navigating the complexities of claims management, providing transparent and reliable solutions.
            </p>
            <p className="text-gray-600 leading-relaxed mt-4">
              At All About Claims, your success is our top priority. We eagerly anticipate the opportunity to partner with you.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-gray-900" data-testid="about-values-section">
        <div className="max-w-5xl mx-auto px-4 py-16 sm:py-20">
          <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-3">Why Choose Us</h2>
          <p className="text-gray-400 text-center max-w-xl mx-auto mb-12">What sets All About Claims apart from the rest.</p>
          <div className="grid sm:grid-cols-3 gap-8">
            {values.map((v) => (
              <div key={v.title} className="text-center">
                <div className="w-12 h-12 rounded-xl bg-teal-500/10 flex items-center justify-center mx-auto mb-4">
                  <v.icon className="w-6 h-6 text-teal-400" />
                </div>
                <h3 className="text-white font-semibold mb-2">{v.title}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer / CTA */}
      <section className="max-w-5xl mx-auto px-4 py-16 sm:py-20 text-center" data-testid="about-cta-section">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">Ready to Get Started?</h2>
        <p className="text-gray-500 max-w-md mx-auto mb-8">Join other trucking companies for a hassle-free claims experience.</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/login" className="inline-flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-medium rounded-lg px-6 py-3 transition-colors" data-testid="about-cta-login">
            Submit a Claim
          </Link>
          <Link to="/pricing" className="inline-flex items-center justify-center gap-2 border border-gray-300 hover:border-gray-400 text-gray-700 font-medium rounded-lg px-6 py-3 transition-colors" data-testid="about-cta-pricing">
            View Pricing
          </Link>
        </div>
      </section>

      {/* Copyright */}
      <div className="border-t border-gray-200 py-6 text-center">
        <p className="text-xs text-gray-400">Copyright &copy; 2026 All About Claims - All Rights Reserved.</p>
        <div className="flex justify-center gap-4 mt-2 text-xs">
          <Link to="/privacy" className="text-gray-400 hover:text-teal-600 transition-colors">Privacy Policy</Link>
          <Link to="/landing" className="text-gray-400 hover:text-teal-600 transition-colors">Home</Link>
        </div>
      </div>
    </div>
  );
}
